// Personel / İş Ekipmanı Havuzu: firmanın tüm kayıtları, belgelerin bugüne ve talep aralığına göre geçerliliği
import { useMemo, useState } from 'react';
import { Users, Truck, CheckCircle2, AlertTriangle, Clock, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Checkbox } from '../../ui/checkbox';
import { Badge } from '../../ui/badge';
import { EMPLOYEE_DOCS } from '../../employees/constants';
import { fmtTr } from './validityCheck';
import { getEquipmentDocs } from './wizardHelpers';
import { TablePagination, paginate } from './TablePagination';

const todayIso = () => new Date().toLocaleDateString('sv-SE');

const docState = (docs, key, end, suffix = '_validity') => {
  const st = docs?.[key];
  const v = (docs?.[`${key}${suffix}`] || (key === 'mesleki_yeterlilik' ? docs?.[`${key}_date`] : '') || '').slice(0, 10);
  const today = todayIso();
  if (st === 'kapsam_disi') return { tone: 'na', text: 'Kapsam dışı' };
  if (st === 'suresi_doldu' || (st === 'onay' && v && v < today)) return { tone: 'red', text: `Süresi dolmuş${v ? ` – ${fmtTr(v)}` : ''}`, expiring: true };
  if (st === 'onay' && v && end && v < end) return { tone: 'amber', text: `Talep aralığında doluyor – ${fmtTr(v)}`, expiring: true };
  if (st === 'onay') return { tone: 'green', text: v ? `Geçerli – ${fmtTr(v)}` : 'Onaylı' };
  if (st === 'eksik') return { tone: 'red', text: 'Eksik', missing: true };
  return { tone: 'slate', text: 'Onaylanmadı', missing: true };
};

const TONE = {
  green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  red: 'bg-rose-50 text-rose-700 border-rose-200',
  slate: 'bg-slate-50 text-slate-600 border-slate-200',
  na: 'bg-slate-50 text-slate-400 border-slate-200',
};

const GROUPS = (noun) => [
  ...GROUPS_BASE(noun),
  { id: 'off', title: `Bölüm 1'de seçilmeyen türdeki ${noun}`, icon: AlertTriangle, cls: 'text-slate-400' },
];
const GROUPS_BASE = (noun) => [
  { id: 'ok', title: `Talep aralığında çalışması uygun ${noun}`, icon: CheckCircle2, cls: 'text-emerald-700' },
  { id: 'expiring', title: `Belgesi süresi dolan / talep aralığında dolacak ${noun}`, icon: AlertTriangle, cls: 'text-amber-700' },
  { id: 'missing', title: `Belgeleri eksik veya onaylanmamış ${noun}`, icon: Clock, cls: 'text-slate-600' },
];

const KINDS = {
  employee: {
    noun: 'personel', title: 'Personel Havuzu', icon: Users, testId: 'employee-pool-dialog', prefix: 'pool', suffix: '_validity',
    name: (e) => `${e.name} ${e.surname}`, sub: (e) => e.position, defs: (e, req) => (req.length ? EMPLOYEE_DOCS.filter(d => req.includes(d.key)) : EMPLOYEE_DOCS),
  },
  equipment: {
    noun: 'iş ekipmanı', title: 'İş Ekipmanı Havuzu', icon: Truck, testId: 'equipment-pool-dialog', prefix: 'eq-pool', suffix: '_date',
    name: (e) => e.name || 'Ekipman', sub: (e) => [e._kindLabel, e.equipment_type === 'arac' ? 'A Grubu' : 'B Grubu'].filter(Boolean).join(' · '), defs: (e) => getEquipmentDocs(e.equipment_type),
  },
};

const PoolRow = ({ emp, cfg, states, inPermit, checked, onCheck }) => (
  <label className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 ${inPermit || emp.is_blocked || emp._mainFirm || emp._kindOff ? 'bg-slate-50 opacity-60' : 'cursor-pointer hover:bg-slate-50'}`} data-testid={`${cfg.prefix}-row-${emp.id}`}>
    <Checkbox checked={inPermit || checked} disabled={inPermit || emp.is_blocked || emp._mainFirm || emp._kindOff} onCheckedChange={onCheck} className="mt-0.5" data-testid={`${cfg.prefix}-check-${emp.id}`} />
    <div className="flex-1 min-w-0 space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="font-medium text-sm text-slate-800">{cfg.name(emp)}</span>
        {cfg.sub(emp) && <span className="text-xs text-slate-500">{cfg.sub(emp)}</span>}
        {emp.is_blocked && <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-[10px]" data-testid={`${cfg.prefix}-blocked-${emp.id}`}>Bloklu – seçilemez</Badge>}
        {emp._kindOff && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]" data-testid={`${cfg.prefix}-kind-off-${emp.id}`}>Bölüm 1'de seçilmedi – seçilemez</Badge>}
        {inPermit && <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 text-[10px]" data-testid={`${cfg.prefix}-in-permit-${emp.id}`}>Talepte</Badge>}
      </div>
      {emp._mainFirm && <p className="text-[11px] text-slate-500" data-testid={`${cfg.prefix}-main-firm-${emp.id}`}>Ana firma personeli – seçmek için alt firmaya yeni kayıt olarak ekleyin (aynı T.C. ile eklenebilir, belgeleri taşınmaz).</p>}
      {!emp._mainFirm && <div className="flex flex-wrap gap-1.5">
        {states.map(s => (
          <span key={s.key} className={`rounded border px-1.5 py-0.5 text-[11px] ${TONE[s.tone]}`} data-testid={`${cfg.prefix}-doc-${emp.id}-${s.key}`}>
            <b className="font-medium">{s.label}:</b> {s.text}
          </span>
        ))}
      </div>}
    </div>
  </label>
);

export const EmployeePoolDialog = ({ open, onOpenChange, employees, selectedEmployees, requiredDocs = [], formData, onAdd, kind = 'employee', allowedKindLabels = null }) => {
  const cfg = KINDS[kind];
  const [picked, setPicked] = useState([]);
  const [q, setQ] = useState('');
  const [onlyFit, setOnlyFit] = useState(true);
  const fitFilter = !!allowedKindLabels && onlyFit;
  const offCount = employees.filter(e => e._kindOff).length;
  const rows = useMemo(() => employees
    .filter(e => !(fitFilter && e._kindOff))
    .filter(e => cfg.name(e).toLocaleLowerCase('tr-TR').includes(q.toLocaleLowerCase('tr-TR')))
    .map(emp => {
      const states = cfg.defs(emp, requiredDocs).map(d => ({ key: d.key, label: d.label, ...docState(emp.documents, d.key, formData.end_date, cfg.suffix) }));
      if (emp._kindOff) return { emp, states, group: 'off' };
      const group = states.some(s => s.expiring) ? 'expiring' : states.some(s => s.missing) ? 'missing' : 'ok';
      return { emp, states, group };
    }), [employees, cfg, requiredDocs, formData.end_date, q, fitFilter]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const ordered = useMemo(() => GROUPS('').flatMap(g => rows.filter(r => r.group === g.id)), [rows]);
  const pg = paginate(ordered, page, pageSize);
  const groupCount = (id) => rows.filter(r => r.group === id).length;
  const selectedIds = new Set(selectedEmployees.map(e => e.id));
  const toggle = (id, c) => setPicked(prev => (c ? [...prev, id] : prev.filter(x => x !== id)));
  const close = (o) => { if (!o) { setPicked([]); setQ(''); setPage(1); setOnlyFit(true); } onOpenChange(o); };
  const add = () => { onAdd(employees.filter(e => picked.includes(e.id))); close(false); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid={cfg.testId}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><cfg.icon className="h-5 w-5" />{cfg.title}</DialogTitle>
          <DialogDescription>
            Talep aralığı: <b>{fmtTr(formData.start_date)} – {fmtTr(formData.end_date)}</b>. Belgeler bugünün tarihine ve talep aralığına göre kontrol edildi
            {kind === 'equipment' ? '.' : requiredDocs.length ? ' (seçilen iş tipi/çeşidi için zorunlu belgeler).' : ' (bu çalışma alanında zorunlu belge tanımlı değil; tüm belgeler gösteriliyor).'}
          </DialogDescription>
        </DialogHeader>
        {allowedKindLabels && (
          <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900" data-testid={`${cfg.prefix}-allowed-kinds`}>
            <b>Bölüm 1'de seçtiğiniz ekipman türleri:</b> {allowedKindLabels.join(', ') || '-'}
            <p className="text-xs text-sky-700">Yalnızca bu türlerdeki ekipmanlar talebe eklenebilir. Farklı bir tür için Bölüm 1'deki seçimi güncelleyin.</p>
          </div>
        )}
        {allowedKindLabels && (
          <label className="flex w-fit cursor-pointer select-none items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800" data-testid={`${cfg.prefix}-only-fit-label`}>
            <Checkbox checked={onlyFit} onCheckedChange={(v) => { setOnlyFit(!!v); setPage(1); }} data-testid={`${cfg.prefix}-only-fit`} />
            Sadece uyumlu (seçilebilir) ekipmanları göster
            {offCount > 0 && <span className="text-xs font-normal text-emerald-700">({offCount} uyumsuz {onlyFit ? 'gizlendi' : 'gösteriliyor'})</span>}
          </label>
        )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder={kind === 'equipment' ? 'Ekipman ara...' : 'Personel ara...'} className="pl-10" data-testid={`${cfg.prefix}-search`} />
        </div>
        <div className="flex-1 overflow-y-auto space-y-5 pr-1">
          {GROUPS(cfg.noun).map(g => {
            const list = pg.rows.filter(r => r.group === g.id);
            if (!list.length) return null;
            return (
              <section key={g.id} className="space-y-2" data-testid={`${cfg.prefix}-group-${g.id}`}>
                <h4 className={`flex items-center gap-2 text-sm font-semibold ${g.cls}`}><g.icon className="h-4 w-4" />{g.title} ({groupCount(g.id)})</h4>
                {list.map(r => (
                  <PoolRow key={r.emp.id} emp={r.emp} cfg={cfg} states={r.states} inPermit={selectedIds.has(r.emp.id)} checked={picked.includes(r.emp.id)} onCheck={(c) => toggle(r.emp.id, c)} />
                ))}
              </section>
            );
          })}
          {!rows.length && <p className="text-center text-sm text-slate-500 py-8" data-testid={`${cfg.prefix}-empty`}>{kind === 'equipment' ? (fitFilter ? 'Bölüm 1\'de seçtiğiniz türlerde kayıtlı ekipman yok. "Yeni Ekipman Ekle" ile ekleyebilirsiniz.' : 'Ekipman bulunamadı') : 'Personel bulunamadı'}</p>}
        </div>
        <TablePagination total={ordered.length} page={pg.cur} totalPages={pg.totalPages} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} prefix={cfg.prefix} />
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} data-testid={`${cfg.prefix}-cancel`}>Kapat</Button>
          <Button onClick={add} disabled={!picked.length} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`${cfg.prefix}-add-btn`}>
            Seçilenleri Talebe Ekle{picked.length ? ` (${picked.length})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
